/**
 * 모든 서버 에러를 화면이 한 가지 모양으로 다루도록 정규화한 에러.
 * - http: 서버가 4xx/5xx로 응답 / network: 응답 자체를 못 받음(연결 끊김·CORS 차단)
 * - timeout: 제한 시간 초과 / aborted: 화면 이탈 등으로 요청 취소 / config: 서버 주소 미설정
 * - invalid-response: 성공 응답인데 약속한 형식이 아님(HTML·깨진 JSON·봉투 누락 — 주소·프록시 설정 오류 신호)
 * - unknown: 그 밖의 예외(토큰 조회 실패·훅 버그 등) — 원인을 모르므로 재시도하지 않는다
 */
export type ApiErrorKind =
  | 'http'
  | 'network'
  | 'timeout'
  | 'aborted'
  | 'config'
  | 'invalid-response'
  | 'unknown';

export interface ApiErrorInit {
  kind: ApiErrorKind;
  service: string;
  message: string;
  status?: number;
  /** 서버가 준 에러 코드 — 예: MG404, INVALID_AUTH */
  code?: string;
  /** 서버가 준 추적 ID — 문의·로그 대조용 */
  requestId?: string;
  /** 401을 인증 처리(onUnauthorized)가 이미 맡았는지 — 맡았으면 화면이 따로 알리지 않는다. */
  authHandled?: boolean;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly service: string;
  readonly status?: number;
  readonly code?: string;
  readonly requestId?: string;
  readonly authHandled: boolean;

  constructor(init: ApiErrorInit) {
    super(init.message, { cause: init.cause });
    this.name = 'ApiError';
    this.kind = init.kind;
    this.service = init.service;
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.authHandled = init.authHandled ?? false;
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/** admin-ds DataTable errorState.isNetworkError 연결용 — 서버 응답이 없었던 실패 */
export const isNetworkError = (error: unknown) =>
  isApiError(error) && (error.kind === 'network' || error.kind === 'timeout');

/** 재시도해도 결과가 같은 실패 — 4xx·설정 누락·취소는 다시 보내지 않는다. */
export function isRetryable(error: unknown) {
  if (!isApiError(error)) {
    return false;
  }
  if (error.kind === 'network' || error.kind === 'timeout') {
    return true;
  }
  return error.kind === 'http' && (error.status ?? 0) >= 500;
}
